import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { getStripeCheckoutStatus } from "@/lib/api";
import { trackPurchase } from "@/lib/metaPixel";

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL = 2500;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { lang, t } = useLang();
  const { clear } = useCart();
  const isFr = lang === "fr";

  const [state, setState] = useState({
    loading: true,
    paid: false,
    expired: false,
    error: null,
    orderNumber: null,
    amount: null,
    currency: "eur",
  });
  const cartClearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({
        ...s,
        loading: false,
        error: isFr ? "Session introuvable." : "Session not found.",
      }));
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getStripeCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          if (!cartClearedRef.current) {
            clear();
            cartClearedRef.current = true;
          }
          // Meta Pixel — Purchase (dedup per session_id via localStorage)
          try {
            const dedupKey = `flv_pixel_purchase_${sessionId}`;
            if (typeof window !== "undefined" && !localStorage.getItem(dedupKey)) {
              localStorage.setItem(dedupKey, "1");
              trackPurchase({
                ids: (data.items || []).map(
                  (it) => `${it.product_id || it.slug || "item"}::${it.variant_id || ""}`,
                ),
                value: Number(data.amount_total || 0),
                currency: (data.currency || "eur").toUpperCase(),
                orderId: data.order_number || sessionId,
                numItems: (data.items || []).reduce(
                  (n, it) => n + (it.quantity || 1),
                  0,
                ) || undefined,
              });
            }
          } catch (_e) {
            /* noop */
          }
          setState({
            loading: false,
            paid: true,
            expired: false,
            error: null,
            orderNumber: data.order_number,
            amount: data.amount_total,
            currency: data.currency,
          });
          return;
        }
        if (data.status === "expired") {
          setState((s) => ({ ...s, loading: false, expired: true }));
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState((s) => ({
            ...s,
            loading: false,
            error: isFr
              ? "Le statut du paiement met du temps à se mettre à jour. Vérifiez votre email."
              : "Payment status is taking a while. Please check your email.",
          }));
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setState((s) => ({
            ...s,
            loading: false,
            error: isFr ? "Erreur lors du contrôle du paiement." : "Error checking payment.",
          }));
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (state.loading) {
    return (
      <section
        data-testid="checkout-pending"
        className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
      >
        <Loader2
          size={36}
          strokeWidth={1.4}
          className="mx-auto text-[#3D2B1F] animate-spin"
        />
        <div className="eyebrow mt-8 mb-3">
          {isFr ? "Confirmation du paiement" : "Confirming payment"}
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
          {isFr ? "Un instant…" : "One moment…"}
        </h1>
        <p className="mt-6 text-[15px] text-[#4a4a4a] max-w-md mx-auto">
          {isFr
            ? "Nous validons votre paiement auprès de Stripe — cela ne prend que quelques secondes."
            : "We're confirming your payment with Stripe — this only takes a few seconds."}
        </p>
      </section>
    );
  }

  if (state.expired) {
    return (
      <section
        data-testid="checkout-expired"
        className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-[#ebe7df] text-[#3D2B1F] flex items-center justify-center mb-8">
          <X size={26} strokeWidth={1.6} />
        </div>
        <h1 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
          {isFr ? "Session expirée." : "Session expired."}
        </h1>
        <p className="mt-6 text-[15px] text-[#4a4a4a] max-w-md mx-auto">
          {isFr
            ? "Aucun paiement n'a été effectué. Vous pouvez réessayer en toute tranquillité."
            : "No payment was taken. You can safely try again."}
        </p>
        <Link
          to="/checkout"
          data-testid="success-retry-checkout"
          className="mt-10 inline-flex items-center gap-3 bg-[#3D2B1F] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#1A1A1A] transition-colors rounded-sm"
        >
          {isFr ? "Réessayer" : "Try again"}
        </Link>
      </section>
    );
  }

  if (state.error) {
    return (
      <section
        data-testid="checkout-error"
        className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
      >
        <h1 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tight">
          {isFr ? "Statut indisponible" : "Status unavailable"}
        </h1>
        <p className="mt-6 text-[15px] text-[#4a4a4a] max-w-md mx-auto">
          {state.error}
        </p>
        <Link
          to="/"
          data-testid="success-back-home"
          className="mt-10 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F0EB] px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#333] transition-colors rounded-sm"
        >
          {isFr ? "Retour à la boutique" : "Back to shop"}
        </Link>
      </section>
    );
  }

  return (
    <section
      data-testid="checkout-success"
      className="max-w-3xl mx-auto px-6 sm:px-10 py-24 md:py-32 text-center"
    >
      <div className="w-16 h-16 mx-auto rounded-full bg-[#3D2B1F] text-[#F5F0EB] flex items-center justify-center mb-8">
        <Check size={26} strokeWidth={1.6} />
      </div>
      <div className="eyebrow mb-4">{t.checkout.success}</div>
      <h1 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
        {t.checkout.successMsg}
      </h1>
      {state.orderNumber && (
        <div className="mt-10 inline-block border border-[#dcd7ce] bg-[#ebe7df]/50 px-8 py-6 rounded-sm">
          <div className="eyebrow mb-2">{t.checkout.orderNumber}</div>
          <div
            data-testid="order-number"
            className="font-serif-display text-2xl tracking-wider"
          >
            {state.orderNumber}
          </div>
        </div>
      )}
      {state.amount != null && (
        <div className="mt-6 text-sm text-[#4a4a4a]">
          {isFr ? "Montant débité" : "Amount charged"} :{" "}
          <span className="text-[#3D2B1F] font-medium">
            {Number(state.amount).toFixed(2)} {(state.currency || "eur").toUpperCase()}
          </span>
        </div>
      )}
      <div className="mt-10">
        <Link
          to="/"
          data-testid="continue-shopping"
          className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F5F0EB] px-8 py-4 text-xs tracking-[0.22em] uppercase hover:bg-[#333] transition-colors rounded-sm"
        >
          {t.checkout.continueShopping}
        </Link>
      </div>

      <div className="mt-6">
        <Link
          to="/legal/retractation"
          data-testid="success-withdrawal-link"
          className="inline-flex items-center gap-2 border border-[#3D2B1F]/40 text-[#3D2B1F] px-6 py-3 text-xs tracking-[0.18em] uppercase rounded-md hover:bg-[#3D2B1F] hover:text-white transition-colors"
        >
          {t.product.exerciseWithdrawal}
        </Link>
      </div>
    </section>
  );
};

export default CheckoutSuccess;
